import { queryKeys, useHttpClient } from '@repo/api-client/react';
import { InvoiceSchema } from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { postJSON } from '../internal/http';

/**
 * Duplicate an existing invoice into a brand-new draft.
 *
 * The "void & reissue" workflow: when an already-sent invoice has
 * an error and we don't have credit notes yet, the user issues a
 * new draft cloned from the source, fixes the typo, sends the
 * replacement, and (separately) cancels the original. This hook
 * just kicks off the duplicate \u2014 the cancel happens through
 * `useTransitionInvoice('cancelled')` if/when the user opts to.
 *
 * Server side: see `InvoicesService.duplicate`. New draft gets a
 * freshly reserved invoice number, today's issue date, and a
 * deep clone of the source's line items and config.
 *
 * Idempotency-keyed on `invoice:duplicate:<orgId>:<sourceId>:<userId>`
 * so a double-tap during a slow round-trip doesn't produce two
 * near-identical drafts.
 */
export function useDuplicateInvoice() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      organizationId,
      invoiceId,
    }: {
      organizationId: string;
      invoiceId: string;
    }) =>
      postJSON(
        http,
        `/orgs/${organizationId}/invoices/${invoiceId}/duplicate`,
        {},
        InvoiceSchema,
      ),
    onSuccess: (invoice) => {
      // Seed the detail cache for the new draft so the
      // post-duplicate navigation lands on a populated screen.
      queryClient.setQueryData(
        queryKeys.invoices.detail(invoice.orgId, invoice.id),
        invoice,
      );
      // The org's invoice list now has one more row; nothing else
      // about the source invoice changed, so we don't touch its
      // detail cache.
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.all(invoice.orgId),
      });
    },
  });

  return {
    duplicateInvoice: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
